/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'

import {
  Modal,
  Typography,
  Space,
  message,
  Button,
  Checkbox,
  Divider,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Flex,
  Avatar,
} from 'antd'
import {
  ClockCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  FileUnknownOutlined,
  PicLeftOutlined,
} from '@ant-design/icons'

import dayjs from 'dayjs'

import api from '~/services/api'
import { ShowError } from '~/utils/errors/apiErrors'

import { useCompanies } from '~/hooks/Companies/useCompanies'
import { useScale } from '~/hooks/Scales/useScale'
import { useLocale } from '~/hooks/locale/useLocale'

import ScaleCalendarDrawer from '~/components/DataManipulation/Scales/ScaleCalendar/ScaleCalendarDrawer'
import { translate } from '~/utils/locale'
import { PopconfirmContainer } from './styles'

interface IScaleModal {
  event: {
    id: string
    title: string
    type: string
    description?: string
    group_id?: string
    day: string
    checkin: dayjs.Dayjs
    checkout: dayjs.Dayjs
    checkinFormatted: string
    checkoutFormatted: string
  }
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
}

const ScaleModal: React.FC<IScaleModal> = ({ event, visible, setVisible }) => {
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [excludeAll, setExcludeAll] = useState(false)
  const [close, setClose] = useState(false)

  const { scaleId } = useParams<{ scaleId: string }>()

  const { selectedCompany } = useCompanies()
  const { scaleRefetch } = useScale()
  const { locale } = useLocale()

  const { Text, Title } = Typography

  const handleOk = () => {
    setVisible(false)
  }

  const handleCancel = useCallback(() => {
    setVisible(false)
    setClose(false)
  }, [setVisible, setClose])

  const handleDelete = async (id: string) => {
    try {
      setConfirmLoading(true)
      await api.delete(
        `/company/${selectedCompany}/schedule/${scaleId}/schedule-time/${id}`,
      )
      setConfirmLoading(false)
      setVisible(false)
      scaleRefetch()
      message.success(translate('ScalesCalendarModal.successMessage'))
    } catch (err: any) {
      ShowError(
        err.message,
        translate('ScalesCalendarModal.errorMessage'),
        locale,
      )

      setConfirmLoading(false)
    }
  }

  const handleDeleteByGroup = async (id: string) => {
    try {
      setConfirmLoading(true)
      await api.delete(
        `/company/${selectedCompany}/schedule/${scaleId}/schedule-time-group/${id}`,
      )
      setConfirmLoading(false)
      scaleRefetch()
      setVisible(false)
      setExcludeAll(false)
      message.success(translate('ScalesCalendarModal.successMessage'))
    } catch (err: any) {
      ShowError(
        err.message,
        translate('ScalesCalendarModal.errorMessage'),
        locale,
      )

      setConfirmLoading(false)
    }
  }

  useEffect(() => {
    if (close) {
      handleCancel()
    }
  }, [close, handleCancel])

  return (
    <Modal
      title={
        <>
          Escala
          <Divider style={{ margin: '10px 0' }} />
        </>
      }
      width={700}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={[
        <Space key="buttons">
          <ScaleCalendarDrawer
            key="CalendarDrawer"
            selectedDay={dayjs(event.day)}
            listData={event}
            setClose={setClose}
          />
          {event.group_id ? (
            <Tooltip title={translate('ScalesCalendarModal.deleteTooltip')}>
              <Popconfirm
                onConfirm={
                  excludeAll
                    ? () => handleDeleteByGroup(event.group_id!)
                    : () => handleDelete(event.id)
                }
                title={
                  <PopconfirmContainer>
                    {translate('ScalesCalendarModal.deletePopconfirm')}
                    <Checkbox
                      onChange={(e) => {
                        setExcludeAll(e.target.checked)
                      }}
                      checked={excludeAll}
                    >
                      {translate('ScalesCalendarModal.deleteCheckbox')}
                    </Checkbox>
                  </PopconfirmContainer>
                }
              >
                <Button
                  type="primary"
                  icon={<DeleteOutlined />}
                  danger
                  loading={confirmLoading}
                />
              </Popconfirm>
            </Tooltip>
          ) : (
            ''
          )}
          <Tooltip title="Fechar">
            <Button
              type="default"
              onClick={handleCancel}
              icon={<CloseOutlined style={{ fontSize: 14 }} />}
            />
          </Tooltip>
        </Space>,
      ]}
    >
      <Row>
        <Col span={12}>
          <Flex gap={10}>
            <Avatar
              shape="square"
              icon={
                <ClockCircleOutlined
                  style={{ fontSize: 12, color: '#464545' }}
                />
              }
              size="small"
              style={{ background: '#DADADA' }}
            />
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                {translate('ScalesCalendarModal.timeStringOne')}
              </Title>
              <Text>{event.checkinFormatted}</Text>
            </div>
          </Flex>
        </Col>

        <Col span={12}>
          <Flex gap={10}>
            <Avatar
              shape="square"
              icon={
                <ClockCircleOutlined
                  style={{ fontSize: 12, color: '#464545' }}
                />
              }
              size="small"
              style={{ background: '#DADADA' }}
            />
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                {translate('ScalesCalendarModal.timeStringTwo')}
              </Title>
              <Text> {event.checkoutFormatted}</Text>
            </div>
          </Flex>
        </Col>
      </Row>

      <Row style={{ marginTop: 14 }}>
        <Col span={12}>
          <Flex gap={10}>
            <Avatar
              shape="square"
              icon={
                <PicLeftOutlined style={{ fontSize: 12, color: '#464545' }} />
              }
              size="small"
              style={{ background: '#DADADA' }}
            />
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                {translate('ScalesCalendarModal.descriptionLabel')}
              </Title>
              {event.description ? (
                event.description
              ) : (
                <Text type="secondary">
                  <Space size="small">
                    <FileUnknownOutlined />
                    N/A
                  </Space>
                </Text>
              )}
            </div>
          </Flex>
        </Col>
      </Row>
    </Modal>
  )
}

export default ScaleModal
